import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaCloudDownloadAlt as FaCloudDownloadRaw,
  FaChevronDown as FaChevronDownRaw,
  FaUsers as FaUsersRaw,
  FaBolt as FaBoltRaw,
  FaUserSlash as FaUserSlashRaw,
  FaPlus as FaPlusRaw,
} from 'react-icons/fa';
import MarketingHeader from '../components/MarketingHeader';
import SyncClearstreamButton from '../components/SyncClearstreamButton';
import {
  type MarketingSubscriber,
  type MarketingSubscriptionChannel,
  useAssignSubscriberToChannel,
  useCreateMarketingSubscriber,
  useMarketingSubscribers,
  useMarketingSubscriptionChannels,
} from '../hooks/useMarketing';
import {
  MarketingPageShell,
  TableCard,
  TableWrap,
  Table,
  TableFooter,
  PrimaryButton,
  SecondaryButton,
  PaginationButton,
} from '../styles/marketingSharedStyles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCloudDownload: any = FaCloudDownloadRaw;
const FaChevronDown: any = FaChevronDownRaw;
const FaUsers: any = FaUsersRaw;
const FaBolt: any = FaBoltRaw;
const FaUserSlash: any = FaUserSlashRaw;
const FaPlus: any = FaPlusRaw;

const PageWrap = styled.div`
  min-height: 100%;
  background: var(--background);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 1rem;
  overflow-x: clip;
`;

const Content = styled(MarketingPageShell).attrs({ as: 'main' })`
  flex: 0 0 auto;
  margin-top: 1rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  margin: 0 0 0.25rem 0;
  font-size: clamp(1.35rem, 2.3vw, 1.85rem);
  font-weight: 700;
  color: var(--onSurface);
`;

const PageSub = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: var(--onSurfaceVariant);
`;

const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  @media (max-width: 760px) {
    width: 100%;
    > * {
      flex: 1 1 0;
      min-width: 0;
      justify-content: center;
    }
  }
`;


const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%), var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.16);
  min-width: 0;
`;

const KpiIcon = styled.div<{ $red?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${({ $red }) => ($red ? 'var(--errorContainer)' : 'var(--surfaceVariant)')};
  color: ${({ $red }) => ($red ? 'var(--error)' : 'var(--primary)')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const KpiContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const KpiLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.35rem;
`;

const KpiValue = styled.div`
  font-size: clamp(1.2rem, 2vw, 1.65rem);
  font-weight: 700;
  color: var(--onSurface);
  margin-bottom: 0.35rem;
`;

const KpiChange = styled.span<{ $positive: boolean }>`
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ $positive }) => ($positive ? 'var(--success)' : 'var(--error)')};
  margin-bottom: 0.5rem;
`;

const KpiBar = styled.div<{ $red?: boolean }>`
  height: 4px;
  background: var(--outline);
  border-radius: 2px;
  overflow: hidden;
`;

const KpiBarFill = styled.div<{ $pct: number; $red?: boolean }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, $pct)}%;
  background: ${({ $red }) => ($red ? 'var(--error)' : 'var(--primary)')};
  border-radius: 2px;
`;

const FilterTabs = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  min-width: 0;
`;

const FilterTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline);
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surfaceVariant)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurfaceVariant)')};
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: ${({ $active }) => ($active ? 'var(--primaryDark)' : 'var(--outline)')};
    color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--onSurface)')};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.5rem;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 35px rgba(0, 0, 0, 0.24);
`;

const ModalTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--onSurface);
`;

const ModalField = styled.div`
  margin-bottom: 1rem;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.35rem;
`;

const ModalInput = styled.input`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  background: var(--surface);
  color: var(--onSurface);
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ModalSelect = styled.select`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  background: var(--surface);
  color: var(--onSurface);
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const ModalCancelBtn = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-weight: 500;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalSubmitBtn = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: var(--primaryDark);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--onSurfaceVariant);
  padding: 1.5rem;
`;

const HelperText = styled.p`
  margin: 0.35rem 0 0 0;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const ImportStats = styled.div`
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
`;


const SegmentPill = styled.span<{ $variant?: 'purple' | 'yellow' | 'blue' | 'green' | 'gray' }>`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  margin-right: 0.35rem;
  margin-bottom: 0.15rem;
  background: ${({ $variant }) =>
    $variant === 'purple'
      ? 'var(--surfaceVariant)'
      : $variant === 'yellow'
        ? 'var(--warningContainer)'
        : $variant === 'blue'
          ? 'var(--surfaceVariant)'
          : $variant === 'green'
            ? 'var(--successContainer)'
            : 'var(--elevation2)'};
  color: ${({ $variant }) =>
    $variant === 'purple'
      ? 'var(--primary)'
      : $variant === 'yellow'
        ? 'var(--onWarningContainer)'
        : $variant === 'blue'
          ? 'var(--primary)'
          : $variant === 'green'
            ? 'var(--success)'
            : 'var(--onSurfaceVariant)'};
`;

const PageFooter = styled.footer`
  text-align: center;
  padding: 1.5rem;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const SEGMENT_VARIANTS: Record<string, 'purple' | 'yellow' | 'blue' | 'green' | 'gray'> = {
  'VIP Club': 'purple',
  'Holiday 24': 'blue',
  'New Customer': 'yellow',
  Purchased: 'green',
  Standard: 'gray',
};

const PAGE_SIZE = 5;
const EMPTY_CHANNELS: MarketingSubscriptionChannel[] = [];
const EMPTY_SUBSCRIBERS: MarketingSubscriber[] = [];
type ImportRow = { phone: string; firstName: string | null; lastName: string | null };

const formatPhoneE164 = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.startsWith('+')
    ? `+${trimmed.slice(1).replace(/\D/g, '')}`
    : `+${trimmed.replace(/\D/g, '')}`;
  return /^\+[1-9]\d{6,14}$/.test(normalized) ? normalized : null;
};

export default function SubscribersPage() {
  const subscribersQuery = useMarketingSubscribers();
  const channelsQuery = useMarketingSubscriptionChannels();
  const createSubscriber = useCreateMarketingSubscriber();
  const assignSubscriberToChannel = useAssignSubscriberToChannel();

  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [importChannelId, setImportChannelId] = useState('');
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const channels = channelsQuery.data ?? EMPTY_CHANNELS;
  const subscribers = subscribersQuery.data ?? EMPTY_SUBSCRIBERS;
  const filtered = useMemo(() => {
    if (activeFilter === 'all') return subscribers;
    return subscribers.filter((subscriber) =>
      (subscriber.channel_memberships ?? []).some(
        (membership) => !membership.unsubscribed_at && membership.channel_id === activeFilter
      )
    );
  }, [activeFilter, subscribers]);

  const totalSubscribers = subscribers.length;
  const activeThisMonth = subscribers.filter((subscriber) => {
    const updatedAt = new Date(subscriber.updated_at);
    const now = new Date();
    return updatedAt.getMonth() === now.getMonth() && updatedAt.getFullYear() === now.getFullYear();
  }).length;
  const unsubscribedCount = subscribers.filter((subscriber) =>
    (subscriber.channel_memberships ?? []).some((membership) => Boolean(membership.unsubscribed_at))
  ).length;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageIndex = Math.min(currentPage, totalPages);
  const start = filtered.length === 0 ? 0 : (pageIndex - 1) * PAGE_SIZE + 1;
  const end = Math.min(pageIndex * PAGE_SIZE, filtered.length);
  const paginated = filtered.slice((pageIndex - 1) * PAGE_SIZE, pageIndex * PAGE_SIZE);

  const handleAddSubscriber = async (event: React.FormEvent) => {
    event.preventDefault();
    const phoneE164 = formatPhoneE164(newPhone);
    if (!phoneE164) {
      window.alert('Please enter a valid phone number (E.164 format, e.g. +14075551234).');
      return;
    }
    if (!newChannelId) {
      window.alert('Please select a subscription channel.');
      return;
    }
    try {
      const created = await createSubscriber.mutateAsync({
        phoneE164,
        firstName: newFirstName.trim() || null,
        lastName: newLastName.trim() || null,
        source: 'manual',
      });
      await assignSubscriberToChannel.mutateAsync({
        subscriberId: created.id,
        channelId: newChannelId,
        source: 'manual',
      });
      setAddModalOpen(false);
      setNewPhone('');
      setNewFirstName('');
      setNewLastName('');
      setNewChannelId('');
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Failed to add subscriber.');
    }
  };

  const parseCsvLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values.map((value) => value.replace(/^"|"$/g, '').trim());
  };

  const handleCsvFileSelected = async (file: File) => {
    const raw = await file.text();
    const lines = raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length < 2) {
      throw new Error('CSV must include a header row and at least one data row.');
    }
    const headers = parseCsvLine(lines[0]).map((header) => header.toLowerCase());
    const phoneIdx = headers.findIndex((h) => ['phone', 'phone_number', 'phonenumber', 'mobile'].includes(h));
    const firstNameIdx = headers.findIndex((h) => ['first_name', 'firstname', 'first'].includes(h));
    const lastNameIdx = headers.findIndex((h) => ['last_name', 'lastname', 'last'].includes(h));
    const nameIdx = headers.findIndex((h) => ['name', 'full_name', 'fullname'].includes(h));
    if (phoneIdx < 0) {
      throw new Error('CSV must include a phone column (phone, phone_number, or mobile).');
    }

    const parsedRows: ImportRow[] = [];
    for (const line of lines.slice(1)) {
      const cols = parseCsvLine(line);
      const phone = cols[phoneIdx] ?? '';
      if (!phone.trim()) continue;

      let firstName = firstNameIdx >= 0 ? (cols[firstNameIdx] ?? '').trim() : '';
      let lastName = lastNameIdx >= 0 ? (cols[lastNameIdx] ?? '').trim() : '';
      if (!firstName && !lastName && nameIdx >= 0) {
        const fullName = (cols[nameIdx] ?? '').trim();
        if (fullName) {
          const parts = fullName.split(/\s+/);
          firstName = parts[0] ?? '';
          lastName = parts.slice(1).join(' ') || '';
        }
      }
      parsedRows.push({
        phone,
        firstName: firstName || null,
        lastName: lastName || null,
      });
    }
    if (parsedRows.length === 0) {
      throw new Error('No valid data rows found in CSV.');
    }
    setImportRows(parsedRows);
    setImportFileName(file.name);
  };

  const handleImportList = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!importChannelId) {
      window.alert('Please select a subscription channel for this import.');
      return;
    }
    if (importRows.length === 0) {
      window.alert('Please upload a CSV file first.');
      return;
    }

    setImporting(true);
    let successCount = 0;
    let failedCount = 0;
    const failedSamples: string[] = [];
    try {
      for (const row of importRows) {
        const phoneE164 = formatPhoneE164(row.phone);
        if (!phoneE164) {
          failedCount += 1;
          if (failedSamples.length < 5) failedSamples.push(row.phone);
          continue;
        }
        try {
          const subscriber = await createSubscriber.mutateAsync({
            phoneE164,
            firstName: row.firstName,
            lastName: row.lastName,
            source: 'import',
          });
          await assignSubscriberToChannel.mutateAsync({
            subscriberId: subscriber.id,
            channelId: importChannelId,
            source: 'import',
          });
          successCount += 1;
        } catch {
          failedCount += 1;
          if (failedSamples.length < 5) failedSamples.push(phoneE164);
        }
      }
      const failureDetail =
        failedSamples.length > 0 ? `\nFailed examples: ${failedSamples.join(', ')}` : '';
      window.alert(`Import complete. Added/updated ${successCount} subscriber(s). Failed: ${failedCount}.${failureDetail}`);
      setImportModalOpen(false);
      setImportRows([]);
      setImportFileName('');
      setImportChannelId('');
    } finally {
      setImporting(false);
    }
  };

  return (
    <PageWrap>
      <MarketingHeader activeTab="subscribers" rightSlot={<SyncClearstreamButton />} />

      <Content>
        <TitleRow>
          <TitleBlock>
            <PageTitle>Subscribers</PageTitle>
            <PageSub>Manage your SMS marketing audience and audience segments.</PageSub>
          </TitleBlock>
          <ActionBtns>
            <SecondaryButton type="button" onClick={() => setImportModalOpen(true)}>
              <FaCloudDownload size={16} /> Import List
            </SecondaryButton>
            <PrimaryButton type="button" onClick={() => setAddModalOpen(true)}>
              <FaPlus size={14} /> Add Subscriber
            </PrimaryButton>
          </ActionBtns>
        </TitleRow>

        <KpiGrid>
          <KpiCard>
            <KpiIcon>
              <FaUsers size={20} />
            </KpiIcon>
            <KpiContent>
              <KpiLabel>Total Subscribers</KpiLabel>
              <KpiValue>{totalSubscribers.toLocaleString()}</KpiValue>
              <KpiChange $positive={totalSubscribers > 0}>Active records</KpiChange>
              <KpiBar>
                <KpiBarFill $pct={100} />
              </KpiBar>
            </KpiContent>
          </KpiCard>
          <KpiCard>
            <KpiIcon>
              <FaBolt size={20} />
            </KpiIcon>
            <KpiContent>
              <KpiLabel>Active This Month</KpiLabel>
              <KpiValue>{activeThisMonth.toLocaleString()}</KpiValue>
              <KpiChange $positive={activeThisMonth > 0}>Updated this month</KpiChange>
              <KpiBar>
                <KpiBarFill $pct={totalSubscribers > 0 ? (activeThisMonth / totalSubscribers) * 100 : 0} />
              </KpiBar>
            </KpiContent>
          </KpiCard>
          <KpiCard>
            <KpiIcon $red>
              <FaUserSlash size={18} />
            </KpiIcon>
            <KpiContent>
              <KpiLabel>Unsubscribed</KpiLabel>
              <KpiValue>{unsubscribedCount.toLocaleString()}</KpiValue>
              <KpiChange $positive={false}>Across all channels</KpiChange>
              <KpiBar $red>
                <KpiBarFill $pct={totalSubscribers > 0 ? (unsubscribedCount / totalSubscribers) * 100 : 0} $red />
              </KpiBar>
            </KpiContent>
          </KpiCard>
        </KpiGrid>

        <FilterTabs>
          <FilterTab $active={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>
            All Subscribers
          </FilterTab>
          {channels.map((channel) => (
            <FilterTab key={channel.id} $active={activeFilter === channel.id} onClick={() => setActiveFilter(channel.id)}>
              {channel.name} <FaChevronDown size={12} />
            </FilterTab>
          ))}
        </FilterTabs>

        <TableCard>
          <TableWrap>
            <Table $mobileCards>
              <thead>
                <tr>
                  <th>Phone Number</th>
                  <th>Name</th>
                  <th>Segments</th>
                  <th>Last Activity</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {subscribersQuery.isLoading && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      Loading subscribers...
                    </td>
                  </tr>
                )}
                {subscribersQuery.error && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--error)' }}>
                      Failed to load subscribers.
                    </td>
                  </tr>
                )}
                {!subscribersQuery.isLoading && !subscribersQuery.error && paginated.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState>No subscribers yet for this filter.</EmptyState>
                    </td>
                  </tr>
                )}
                {paginated.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Phone Number">{row.phone_e164}</td>
                    <td data-label="Name">{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td data-label="Segments">
                      {(row.channel_memberships ?? [])
                        .filter((membership) => !membership.unsubscribed_at)
                        .map((membership) => (
                        <SegmentPill
                          key={membership.id}
                          $variant={SEGMENT_VARIANTS[membership.channel?.name ?? ''] ?? 'gray'}
                        >
                          {membership.channel?.name ?? 'Unknown'}
                        </SegmentPill>
                      ))}
                    </td>
                    <td data-label="Last Activity">{new Date(row.updated_at).toLocaleDateString()}</td>
                    <td data-label="Action">{row.source}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <TableFooter>
            <span>
              Showing {start} to {end} of {filtered.length.toLocaleString()} subscribers
            </span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <PaginationButton
                type="button"
                aria-label="Previous page"
                disabled={currentPage <= 1}
                $active={currentPage > 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                ←
              </PaginationButton>
              <PaginationButton
                type="button"
                aria-label="Next page"
                disabled={currentPage >= totalPages}
                $active={currentPage < totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                →
              </PaginationButton>
            </div>
          </TableFooter>
        </TableCard>

        <PageFooter>© 2024 SMS Blast Marketing Platform. All rights reserved.</PageFooter>
      </Content>
      {addModalOpen && (
        <ModalOverlay onClick={() => setAddModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalTitle>Add Subscriber</ModalTitle>
            <form onSubmit={handleAddSubscriber}>
              <ModalField>
                <ModalLabel>Phone number</ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="+14075551234"
                  value={newPhone}
                  onChange={(event) => setNewPhone(event.target.value)}
                  autoFocus
                />
              </ModalField>
              <ModalField>
                <ModalLabel>First name (optional)</ModalLabel>
                <ModalInput
                  type="text"
                  value={newFirstName}
                  onChange={(event) => setNewFirstName(event.target.value)}
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Last name (optional)</ModalLabel>
                <ModalInput
                  type="text"
                  value={newLastName}
                  onChange={(event) => setNewLastName(event.target.value)}
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Subscription channel</ModalLabel>
                <ModalSelect value={newChannelId} onChange={(event) => setNewChannelId(event.target.value)}>
                  <option value="">Select channel</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </ModalSelect>
              </ModalField>
              <ModalActions>
                <ModalCancelBtn type="button" onClick={() => setAddModalOpen(false)}>
                  Cancel
                </ModalCancelBtn>
                <ModalSubmitBtn type="submit" disabled={createSubscriber.isPending || assignSubscriberToChannel.isPending}>
                  {createSubscriber.isPending || assignSubscriberToChannel.isPending ? 'Saving...' : 'Add Subscriber'}
                </ModalSubmitBtn>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
      {importModalOpen && (
        <ModalOverlay onClick={() => setImportModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalTitle>Import Subscribers</ModalTitle>
            <form onSubmit={handleImportList}>
              <ModalField>
                <ModalLabel>CSV file</ModalLabel>
                <ModalInput
                  type="file"
                  accept=".csv,text/csv"
                  ref={fileInputRef}
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    try {
                      await handleCsvFileSelected(file);
                    } catch (error) {
                      window.alert(error instanceof Error ? error.message : 'Unable to parse CSV.');
                      event.target.value = '';
                    }
                  }}
                />
                <HelperText>Headers supported: phone, first_name, last_name (or name).</HelperText>
                {importFileName && (
                  <ImportStats>
                    File: <strong>{importFileName}</strong>
                    <br />
                    Parsed rows: <strong>{importRows.length}</strong>
                  </ImportStats>
                )}
              </ModalField>
              <ModalField>
                <ModalLabel>Subscription channel for this import</ModalLabel>
                <ModalSelect value={importChannelId} onChange={(event) => setImportChannelId(event.target.value)}>
                  <option value="">Select channel</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </ModalSelect>
              </ModalField>
              <ModalActions>
                <ModalCancelBtn type="button" onClick={() => setImportModalOpen(false)}>
                  Cancel
                </ModalCancelBtn>
                <ModalSubmitBtn type="submit" disabled={importing}>
                  {importing ? 'Importing...' : 'Import List'}
                </ModalSubmitBtn>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageWrap>
  );
}
